import { Component, Inject, OnInit } from "@angular/core";
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { NgxSpinnerService } from "ngx-spinner";
import { FormBuilder, FormGroup } from "@angular/forms";
import { GiteaService } from "src/app/core/services/gitea.service";
import { GiteaMigrationStatus } from "src/app/core/models/gitea";

@Component({
  selector: "app-add-local-repo",
  templateUrl: "./add-local-repo.component.html",
  styleUrl: "./add-local-repo.component.scss",
})
export class AddLocalRepoComponent implements OnInit {
  migrationForm: FormGroup;
  isLoading = false;
  migrationResult: GiteaMigrationStatus | null = null;
  showAdvanced = false;

  constructor(
    private fb: FormBuilder,
    private giteaService: GiteaService,
    private dialogRef: MatDialogRef<AddLocalRepoComponent>,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.migrationForm = this.fb.group({
      // Basic fields
      huggingFaceUrl: [
        "",
        [
          Validators.required,
          Validators.pattern(/^https:\/\/huggingface\.co\/.*$/),
        ],
      ],
      repoName: [
        "",
        [Validators.required, Validators.pattern(/^[a-zA-Z0-9_.-]+$/)],
      ],
      repoOwner: ["", [Validators.required]],
      description: [""],
      isPrivate: [false],

      // Authentication fields
      needsAuth: [false],
      hfUsername: [""],
      hfAccessToken: [""],

      // Advanced options
      enableWiki: [true],
      enableReleases: [true],
      enableLFS: [true],
      createAsMirror: [false],
    });
  }

  ngOnInit(): void {
    // Auto-generate repo name from URL
    this.migrationForm.get("huggingFaceUrl")?.valueChanges.subscribe((url) => {
      if (url && url.match(/^https:\/\/huggingface\.co\/([^\/]+)\/([^\/]+)$/)) {
        const parts = url.split("/");
        const suggestedName = parts[parts.length - 1];
        if (!this.migrationForm.get("repoName")?.value) {
          this.migrationForm.patchValue({ repoName: suggestedName });
        }
      }
    });

    // Enable/disable auth fields based on needsAuth checkbox
    this.migrationForm.get("needsAuth")?.valueChanges.subscribe((needsAuth) => {
      const hfUsernameControl = this.migrationForm.get("hfUsername");
      const hfTokenControl = this.migrationForm.get("hfAccessToken");

      if (needsAuth) {
        hfUsernameControl?.setValidators([Validators.required]);
        hfTokenControl?.setValidators([
          Validators.required,
          Validators.pattern(/^hf_[a-zA-Z0-9]+$/),
        ]);
      } else {
        hfUsernameControl?.clearValidators();
        hfTokenControl?.clearValidators();
      }

      hfUsernameControl?.updateValueAndValidity();
      hfTokenControl?.updateValueAndValidity();
    });
  }

  onSubmit(): void {
    if (this.migrationForm.valid) {
      this.isLoading = true;
      this.migrationResult = null;

      const formValue = this.migrationForm.value;

      if (formValue.needsAuth && formValue.hfAccessToken) {
        // Use token-based authentication
        this.giteaService
          .migrateFromHuggingFaceWithToken(
            formValue.huggingFaceUrl,
            formValue.repoName,
            formValue.repoOwner,
            formValue.hfAccessToken,
            formValue.isPrivate,
            formValue.description
          )
          .subscribe({
            next: (result) => this.handleMigrationResult(result),
            error: (error) => this.handleMigrationError(error),
          });
      } else if (formValue.needsAuth) {
        // Use username/password authentication
        this.giteaService
          .migrateFromHuggingFace(
            formValue.huggingFaceUrl,
            formValue.repoName,
            formValue.repoOwner,
            formValue.isPrivate,
            formValue.description,
            formValue.hfUsername,
            formValue.hfAccessToken
          )
          .subscribe({
            next: (result) => this.handleMigrationResult(result),
            error: (error) => this.handleMigrationError(error),
          });
      } else {
        // Public repository - no authentication
        this.giteaService
          .migrateFromHuggingFace(
            formValue.huggingFaceUrl,
            formValue.repoName,
            formValue.repoOwner,
            formValue.isPrivate,
            formValue.description
          )
          .subscribe({
            next: (result) => this.handleMigrationResult(result),
            error: (error) => this.handleMigrationError(error),
          });
      }
    } else {
      this.markFormGroupTouched();
    }
  }

  private handleMigrationResult(result: GiteaMigrationStatus): void {
    this.isLoading = false;
    this.migrationResult = result;

    if (result.success) {
      this.snackBar.open("Migration successful!", "Ok", {
        duration: 5000,
      });
      // Close dialog and return the result
      setTimeout(() => {
        this.dialogRef.close({ success: true, result });
      }, 2000);
    } else {
      this.snackBar.open("Migration failed: " + result.message, "Ok", {
        duration: 5000,
      });
    }
  }

  private handleMigrationError(error: any): void {
    this.isLoading = false;
    this.migrationResult = {
      success: false,
      message: "Migration failed due to network error",
      repository_url: null,
      error: error.message || "Unknown error",
    };
    this.snackBar.open("Migration failed due to network error", "Ok", {
      duration: 5000,
    });
  }

  private markFormGroupTouched(): void {
    Object.keys(this.migrationForm.controls).forEach((key) => {
      this.migrationForm.get(key)?.markAsTouched();
    });
  }

  // Dialog methods
  onCancel(): void {
    this.dialogRef.close({ success: false });
  }

  onClose(): void {
    this.dialogRef.close({ success: false });
  }

  // Helper methods for template
  isFieldInvalid(fieldName: string): boolean {
    const field = this.migrationForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.migrationForm.get(fieldName);
    if (field?.errors) {
      if (field.errors["required"]) return `${fieldName} is required`;
      if (field.errors["pattern"]) return `Invalid ${fieldName} format`;
    }
    return "";
  }
}
